import * as React from 'react';
import { EventFrom, SnapshotFrom } from 'xstate';
import { useTranslation } from 'react-i18next';
import { Block } from '@hvcgroep/design-system';
import { registrationDeskMachine } from '../../state-machine';
import OfferBlock from '../OfferBlock';
import gtm from '../../../../shared/helpers/gtm';

import './style.scss';
import { APP_ID } from '../../../../shared/config/constants';

type Props = {
  snapshot: SnapshotFrom<typeof registrationDeskMachine>;
  send: (event: EventFrom<typeof registrationDeskMachine>) => void,
};

function dateOfBirthFormatter(date: Date) {
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  }).format(date);
}

function startDateFormatter(date: Date) {
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}

const ConfirmBlock: React.FC<Props> = (props) => {
  const { t } = useTranslation();

  const {
    snapshot,
    send,
  } = props;

  return (
    <div>
      <Block padding="medium">
        <h2>{t('registration_desk.confirm_block.title')}</h2>
        <div>
          <div className="d-flex button-edit">
            <p className="text-bold mb-0">
              {t('registration_desk.confirm_block.subtitle')}
            </p>
            <button
              type="button"
              className="a no-styling ml-3"
              onClick={() => {
                send({
                  type: 'goBack.usage',
                });
                gtm.trackUserEvent(String(APP_ID), 'Edit registration', t('registration_desk.confirm_block.usage.title'));
              }}
            >
              {(t('registration_desk.buttons.edit'))}
            </button>
          </div>
          <OfferBlock
            sjvGJ={String(snapshot.context.usageGJ)}
            ean={String(snapshot.context.eamData?.Ean)}
            withExplanationLink={false}
            disableLottieReveal={snapshot.context.seenSteps.has('/controle')}
          />
        </div>
        <div className="mt-5">
          <div className="d-flex button-edit">
            <p className="text-bold mb-0">
              {t('registration_desk.confirm_block.personal_details.title')}
            </p>
            <button
              type="button"
              className="a no-styling ml-3"
              onClick={() => {
                send({
                  type: 'goBack.personalDetails',
                });
                gtm.trackUserEvent(String(APP_ID), 'Edit registration', t('registration_desk.confirm_block.personal_details.title'));
              }}
            >
              {t('registration_desk.buttons.edit')}
            </button>
          </div>
          <p className="mb-0">{snapshot.context.firstName} {snapshot.context.infix} {snapshot.context.lastName}</p>
          <p className="mb-0">{dateOfBirthFormatter(snapshot.context.dateOfBirth!)}</p>
          <p className="mb-0">{snapshot.context.email}</p>
          <p className="mb-0">{snapshot.context.tel}</p>
        </div>
        <div className="mt-5">
          <div className="d-flex button-edit">
            <p className="text-bold mb-0">
              {t('registration_desk.confirm_block.delivery_details.title')}
            </p>
            <button
              type="button"
              className="a no-styling ml-3"
              onClick={() => {
                send({
                  type: 'goBack.deliveryDetails',
                });
                gtm.trackUserEvent(String(APP_ID), 'Edit registration', t('registration_desk.confirm_block.delivery_details.title'));
              }}
            >
              {t('registration_desk.buttons.edit')}
            </button>
          </div>
          <p className="mb-0">{snapshot.context.eamData?.Street} {snapshot.context.eamData?.Housenumber}{snapshot.context.eamData?.HousenumberExt}</p>
          <p className="mb-0">{snapshot.context.eamData?.PostalCode}, {snapshot.context.eamData?.City}</p>
          <p className="mb-0">{t('registration_desk.confirm_block.delivery_details.start_date')}: {startDateFormatter(snapshot.context.startDate!)}</p>
          {snapshot.context.billingAddress && (
          <>
            <div className="d-flex mt-3">
              <p className="mb-0 text-italic">
                {t('registration_desk.confirm_block.billing_address.title')}
              </p>
            </div>
            <p className="mb-0">{snapshot.context.billingAddress.straatnaam} {snapshot.context.billingAddress.huisnummer}{snapshot.context.billingAddress?.huisletter}</p>
            <p className="mb-0">{snapshot.context.billingAddress.postcode}, {snapshot.context.billingAddress.woonplaatsnaam}</p>
          </>
          )}
        </div>
        <div className="mt-5">
          <div className="d-flex button-edit">
            <p className="text-bold mb-0">
              {t('registration_desk.confirm_block.payment_details.title')}
            </p>
            <button
              type="button"
              className="a no-styling ml-3"
              onClick={() => {
                send({
                  type: 'goBack.paymentDetails',
                });
                gtm.trackUserEvent(String(APP_ID), 'Edit registration', t('registration_desk.confirm_block.payment_details.title'));
              }}
            >
              {t('registration_desk.buttons.edit')}
            </button>
          </div>
          {snapshot.context.paymentMethod === 'ideal' ? (
            <p className="mb-0">{t('registration_desk.confirm_block.payment_details.ideal_payment')} {snapshot.context.iban}</p>
          ) : (
            <p className="mb-0">{t('registration_desk.confirm_block.payment_details.auto_incasso')} {snapshot.context.iban}</p>
          )}
        </div>
      </Block>
    </div>
  );
};

export default ConfirmBlock;
